import React, { useState, useEffect } from "react";
import OrderItem from "components/OrderItem";
import CustomFormatNumber from "components/CustomFormatNumber";
import PrevButton from "components/PrevButton";
import NextButton from "components/NextButton";
import Input from "components/Input";
import TwoCellRow from "components/TwoCellRow";
import FormatDateWithLocale from "components/FormatDateWithLocale";
import { numberRegex } from "constants/validationRegex";
import cn from "classnames";
import { acceptOrder } from "api";
import { sendFormError } from "i18n/messages/errors";
import { useFormattedMessage, useSideModalScrollToTop } from "hooks";

const messages = {
    requiredField: "required_field",
    soOTPAcceptMainHeading: "sell_digital_shares",
    soAcceptHeading: "accept_purchase_offer",
    soAcceptButton: "accept_purchase_offer_button",
    soAcceptTileTitle: "your_choice",
    soAcceptTitle1: "buy",
    soAcceptTitle2: "shares_now_for",
    soAcceptTitle3: "so_accept_title3",
    soAcceptContent: "sales_offer_approved",
    soEditDigitalSharesLabel: "digital_shares_to_be_shared",
    boEditDigitalSharesPlaceholder: "number_of_shares",
    totalSalesPrice: "total_sales_price",
    notEnoughBalance: "not_enough_balance",
    minimumAmountOfShares: "minimum_amount_of_shares",
    minimumUserShares: "minimum_user_shares",
    invalidShares: "invalid_shares",
    shareShortText: "pc",
    sharesExceeds: "shares_exceeds",
    sharesAvailableAgainText: "all_digital_shares_reserved",
    minInvestShares1: "min_invest_shares1",
    minInvestShares2: "min_invest_shares2",
};

const leftClass = "mb-9 d-block";
const rightClass = "text-14 fw-600 d-block mb-9";

const AcceptOrder = ({
    className = "",
    onBack,
    onForward,
    setCloseTitle,
    stepData,
    userProjectShares,
    backSlideEffect = "",
    minShareTypeWise,
}) => {
    useSideModalScrollToTop();

    const { formatMessage } = useFormattedMessage();
    const [isSubmitting, setSubmitting] = useState("");
    const [splitShares, setSplitShares] = useState(stepData.shares);
    const [totalSalesPrice, setTotalSalesPrice] = useState(0);
    const [status, setStatus] = useState({});
    const [requestError, setRequestError] = useState();

    useEffect(() => {
        setCloseTitle(formatMessage(messages.soOTPAcceptMainHeading));
    }, []);

    useEffect(() => {
        if (splitShares || stepData.price || stepData.pricePerShare) {
            const price = splitShares !== "" ? parseInt(splitShares) * parseFloat(stepData.pricePerShare) : stepData.price;
            price && setTotalSalesPrice(price);
        }
    }, [splitShares, stepData.price, stepData.pricePerShare]);

    const sharesErrorMessage = (_minInvestShare) => {
        return `${formatMessage(messages.minInvestShares1)} ${_minInvestShare} ${formatMessage(messages.minInvestShares2)}`;
    };

    useEffect(() => {
        if (totalSalesPrice || splitShares) {
            if (splitShares !== "" && !numberRegex.test(splitShares)) {
                setRequestError(formatMessage(messages.invalidShares));
            } else if (splitShares !== "" && parseInt(splitShares) < minShareTypeWise) {
                setRequestError(sharesErrorMessage(minShareTypeWise));
            } else if (splitShares !== "" && parseInt(splitShares) > parseInt(stepData.shares)) {
                setRequestError(formatMessage(messages.sharesExceeds));
            } else if (
                splitShares !== "" &&
                parseInt(stepData.shares) - parseInt(splitShares) !== 0 &&
                parseInt(stepData.shares) - parseInt(splitShares) < minShareTypeWise
            ) {
                setRequestError(formatMessage(messages.minimumUserShares));
            } else if (
                splitShares !== "" &&
                (parseInt(splitShares) > parseInt(userProjectShares) ||
                    (parseInt(userProjectShares) - parseInt(splitShares) !== 0 &&
                        parseInt(userProjectShares) - parseInt(splitShares) < minShareTypeWise))
            ) {
                setRequestError(formatMessage(messages.minimumUserShares));
            } else {
                clearError();
            }
        }
    }, [totalSalesPrice, splitShares]);

    useEffect(() => {
        if (status?.requestError) {
            setRequestError(
                status.requestError === "notEnoughBalance"
                    ? formatMessage(messages.notEnoughBalance)
                    : status.requestError === "minimumAmountOfShares"
                    ? sharesErrorMessage(minShareTypeWise)
                    : formatMessage(sendFormError)
            );
        }
    }, [status]);

    const onSubmit = () => {
        try {
            setSubmitting(true);

            const variables = {
                order: {
                    uid: stepData.uid,
                    Shares: splitShares !== "" ? splitShares : stepData.shares,
                },
            };

            acceptOrder(variables)
                .then((res) => {
                    res && setSubmitting(false);

                    if (res?.data?.errors?.length) {
                        setStatus({ requestError: res.data.errors[0].message });
                    }

                    if (!res || res.errors || res.data.errors || res.data.data.errors) return;

                    const order = res?.data?.data?.acceptOrder?.order;

                    return onForward(order);
                })
                .catch(() => {
                    setStatus({ requestError: true });
                    setSubmitting(false);
                });
        } catch (error) {
            setStatus({ requestError: true });
            setSubmitting(false);
        }
    };

    const clearError = () => {
        setStatus({});
        setRequestError("");
    };

    return (
        <>
            <div className={`sell-orders-container ${className} ${backSlideEffect}`}>
                <h3>{formatMessage(messages.soAcceptHeading)}</h3>
                <h4 className="mt-10">{formatMessage(messages.soAcceptTileTitle)}</h4>
                <OrderItem
                    equalTitle={false}
                    orderFlag={stepData.flag}
                    split={stepData.split}
                    tag={stepData.tag}
                    className="mt-0 mb-10 border-active-red"
                    image={stepData.image}
                    title={stepData.title}
                    // city={stepData.city}
                    shares={stepData.shares}
                    price={stepData.price}
                    date={<FormatDateWithLocale date={stepData.date} />}
                    perSharePrice={stepData.pricePerShare}
                />

                {parseInt(stepData.shares) === 0 ? (
                    <p>{formatMessage(messages.sharesAvailableAgainText)}</p>
                ) : (
                    <p>
                        {formatMessage(messages.soAcceptTitle1)} <CustomFormatNumber value={stepData.shares} fixed="0" noSymbol />{" "}
                        {formatMessage(messages.soAcceptTitle2)} {<CustomFormatNumber value={stepData.price} />}{" "}
                        {formatMessage(messages.soAcceptTitle3)}
                    </p>
                )}

                {stepData.split && parseInt(stepData.shares) >= minShareTypeWise * 2 ? (
                    <>
                        <p>{formatMessage(messages.soAcceptContent)}</p>
                        <div className="mt-10">
                            <Input
                                label={formatMessage(messages.soEditDigitalSharesLabel)}
                                className="mb-6"
                                type="text"
                                name="splitShares"
                                placeholder={formatMessage(messages.boEditDigitalSharesPlaceholder)}
                                inputSymbol={formatMessage(messages.shareShortText)}
                                inputSymbolClass="black-color"
                                value={splitShares}
                                onChange={(e) => setSplitShares(e.target.value)}
                                // onBlur={handleBlur}
                                // errorMessage={errors.splitShares ? formatMessage(messages[errors.splitShares]) : null}
                                error={requestError}
                            />
                        </div>
                    </>
                ) : null}
                <TwoCellRow
                    removeLClass
                    removeRClass
                    lClassname={leftClass}
                    rClassname={rightClass}
                    left={formatMessage(messages.totalSalesPrice)}
                    right={<CustomFormatNumber value={totalSalesPrice || stepData.price} />}
                />
            </div>
            {requestError && <span className="error pl-0">{requestError}</span>}
            <div className="bottom-bar-container single-button">
                <PrevButton disable={isSubmitting} onClick={onBack} />
                <NextButton
                    className={cn({
                        "mw-100 mr-1 btn-sell": true,
                        "disable-grey": isSubmitting || requestError || parseInt(stepData.shares) === 0,
                    })}
                    disable={
                        isSubmitting ||
                        requestError ||
                        parseInt(stepData.shares) === 0 ||
                        (parseInt(userProjectShares) < stepData.shares
                            ? parseInt(userProjectShares) >= parseInt(splitShares)
                                ? false
                                : true
                            : false)
                    }
                    title={formatMessage(messages.soAcceptButton)}
                    onClick={onSubmit}
                    isLoading={isSubmitting}
                />
            </div>
        </>
    );
};

export default AcceptOrder;
